(() => {
  'use strict';

  if (customElements.get('auros-variant-mega')) return;

  class AurosVariantMega extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._abort = new AbortController();
      const { signal } = this._abort;

      this.dialog = this.querySelector('.auros-variant-mega__dialog');
      this.openButton = this.querySelector('[data-auros-mega-open]');
      this.closeButtons = this.querySelectorAll('[data-auros-mega-close]');
      this.currentNodes = this.querySelectorAll('[data-auros-current]');

      this._closeTimer = null;
      this._openFrame = null;
      this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this._pageScrollX = 0;
      this._pageScrollY = 0;
      this._pageScrollLocked = false;

      this._keepPageLocked = () => {
        if (!this._pageScrollLocked) return;

        if (
          window.scrollX !== this._pageScrollX ||
          window.scrollY !== this._pageScrollY
        ) {
          window.scrollTo(
            this._pageScrollX,
            this._pageScrollY
          );
        }
      };

      this._preventBackgroundWheel = (event) => {
        if (!this._pageScrollLocked) return;

        const scrollArea = event.target.closest(
          '.auros-variant-mega__grid'
        );

        if (!scrollArea) {
          event.preventDefault();
        }
      };

      this._preventBackgroundTouch = (event) => {
        if (!this._pageScrollLocked) return;

        const scrollArea = event.target.closest(
          '.auros-variant-mega__grid'
        );

        if (!scrollArea) {
          event.preventDefault();
        }
      };

      this._preventBackgroundKeys = (event) => {
        if (!this._pageScrollLocked) return;

        const scrollKeys = new Set([
          ' ',
          'Spacebar',
          'PageUp',
          'PageDown',
          'Home',
          'End',
          'ArrowUp',
          'ArrowDown'
        ]);

        if (!scrollKeys.has(event.key)) return;

        const scrollArea = event.target.closest(
          '.auros-variant-mega__grid'
        );

        if (!scrollArea) {
          event.preventDefault();
        }
      };

      if (!this.dialog || !this.openButton) return;

      this.openButton.addEventListener(
        'click',
        () => this.open(),
        { signal }
      );

      this.closeButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => this.close(),
          { signal }
        );
      });

      this.dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === this.dialog) {
            this.close();
          }
        },
        { signal }
      );

      this.dialog.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.close();
        },
        { signal }
      );

      this.dialog.addEventListener(
        'close',
        () => {
          this.cleanupMotionState();

          document.documentElement.classList.remove(
            'auros-variant-mega-open'
          );

          this.unlockPageScroll();

          this.openButton.setAttribute(
            'aria-expanded',
            'false'
          );
        },
        { signal }
      );

      this.addEventListener(
        'change',
        (event) => {
          const input = event.target.closest(
            'input[is-value][type="radio"]'
          );

          if (!input || !this.contains(input)) return;

          this.syncCurrent(input.value);
        },
        { signal }
      );

      this.syncFromChecked();
      this.openButton.setAttribute('aria-expanded', 'false');
    }

    disconnectedCallback() {
      this._abort?.abort();
      this.clearMotionTimers();
      this.unlockPageScroll();

      this._initialized = false;

      document.documentElement.classList.remove(
        'auros-variant-mega-open'
      );
    }

    getHeaderOffset() {
      const selectors = [
        'header',
        '.hdt-header',
        '[data-header]',
        '[class*="header-sticky"]',
        '[class*="sticky-header"]'
      ];

      const candidates = Array.from(
        document.querySelectorAll(
          selectors.join(',')
        )
      );

      let bottom = 0;

      candidates.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);

        if (
          style.display === 'none' ||
          style.visibility === 'hidden' ||
          rect.width <= 0 ||
          rect.height <= 0
        ) {
          return;
        }

        /*
          Solo interesan elementos visibles en la parte superior.
          Esto evita tomar headers ocultos de menús móviles/offcanvas.
        */
        if (
          rect.bottom > 0 &&
          rect.top < 160
        ) {
          bottom = Math.max(
            bottom,
            Math.min(rect.bottom, 180)
          );
        }
      });

      return Math.max(0, Math.round(bottom));
    }

    syncHeaderOffset() {
      const offset = this.getHeaderOffset();

      this.dialog?.style.setProperty(
        '--auros-mega-header-offset',
        offset + 'px'
      );
    }

    lockPageScroll() {
      if (this._pageScrollLocked) return;

      this._pageScrollX = window.scrollX;
      this._pageScrollY = window.scrollY;
      this._pageScrollLocked = true;

      /*
        No usamos overflow:hidden.
        La barra vertical real permanece visible y conserva su ancho.
        Los intentos de mover la página se bloquean por eventos y,
        como respaldo, se restaura inmediatamente la posición original.
      */
      window.addEventListener(
        'scroll',
        this._keepPageLocked,
        { passive: true }
      );

      document.addEventListener(
        'wheel',
        this._preventBackgroundWheel,
        {
          passive: false,
          capture: true
        }
      );

      document.addEventListener(
        'touchmove',
        this._preventBackgroundTouch,
        {
          passive: false,
          capture: true
        }
      );

      document.addEventListener(
        'keydown',
        this._preventBackgroundKeys,
        true
      );
    }

    unlockPageScroll() {
      if (!this._pageScrollLocked) return;

      this._pageScrollLocked = false;

      window.removeEventListener(
        'scroll',
        this._keepPageLocked
      );

      document.removeEventListener(
        'wheel',
        this._preventBackgroundWheel,
        true
      );

      document.removeEventListener(
        'touchmove',
        this._preventBackgroundTouch,
        true
      );

      document.removeEventListener(
        'keydown',
        this._preventBackgroundKeys,
        true
      );

      window.scrollTo(
        this._pageScrollX,
        this._pageScrollY
      );
    }

    get motionDuration() {
      return this._motionQuery?.matches ? 0 : 145;
    }

    clearMotionTimers() {
      if (this._closeTimer !== null) {
        window.clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }

      if (this._openFrame !== null) {
        window.cancelAnimationFrame(this._openFrame);
        this._openFrame = null;
      }
    }

    cleanupMotionState() {
      this.clearMotionTimers();

      this.dialog?.classList.remove(
        'is-entering',
        'is-visible',
        'is-leaving'
      );
    }

    open() {
      if (!this.dialog) return;

      this.clearMotionTimers();
      this.syncFromChecked();
      this.syncHeaderOffset();
      this.lockPageScroll();

      document.documentElement.classList.add(
        'auros-variant-mega-open'
      );

      this.openButton.setAttribute(
        'aria-expanded',
        'true'
      );

      this.dialog.classList.remove(
        'is-visible',
        'is-leaving'
      );

      this.dialog.classList.add('is-entering');

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) {
          this.dialog.showModal();
        }
      } else {
        this.dialog.setAttribute('open', '');
      }

      this._openFrame = window.requestAnimationFrame(() => {
        this._openFrame = null;

        this.dialog.classList.remove('is-entering');
        this.dialog.classList.add('is-visible');

        const selected =
          this.querySelector(
            'input[is-value][type="radio"]:checked'
          )?.nextElementSibling;

        if (
          selected &&
          typeof selected.scrollIntoView === 'function'
        ) {
          selected.scrollIntoView({
            block: 'nearest',
            inline: 'nearest'
          });
        }

        this.querySelector(
          '[data-auros-mega-close]'
        )?.focus({
          preventScroll: true
        });
      });
    }

    close() {
      if (!this.dialog) return;

      const isOpen =
        this.dialog.open ||
        this.dialog.hasAttribute('open');

      if (!isOpen) return;

      this.clearMotionTimers();

      const duration = this.motionDuration;

      this.dialog.classList.remove(
        'is-entering',
        'is-visible'
      );

      this.dialog.classList.add('is-leaving');

      const finishClose = () => {
        this._closeTimer = null;

        if (
          typeof this.dialog.close === 'function' &&
          this.dialog.open
        ) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');

          this.cleanupMotionState();

          document.documentElement.classList.remove(
            'auros-variant-mega-open'
          );

          this.unlockPageScroll();

          this.openButton.setAttribute(
            'aria-expanded',
            'false'
          );
        }

        window.requestAnimationFrame(() => {
          this.openButton?.focus({
            preventScroll: true
          });
        });
      };

      if (duration === 0) {
        finishClose();
        return;
      }

      this._closeTimer = window.setTimeout(
        finishClose,
        duration
      );
    }

    syncFromChecked() {
      const checked = this.querySelector(
        'input[is-value][type="radio"]:checked'
      );

      if (checked) {
        this.syncCurrent(checked.value);
      }
    }

    syncCurrent(value) {
      if (!value) return;

      this.currentNodes.forEach((node) => {
        node.textContent = value;
      });
    }
  }

  customElements.define(
    'auros-variant-mega',
    AurosVariantMega
  );
})();